import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { chargerSessions, useActivityStore, type Activity } from '../../stores/activityStore';
import { useKidStore, type KidWithDetails } from '../../stores/kidStore';
import { useCartStore } from '../../stores/cartStore';
import { useWaitingListStore } from '../../stores/waitingListStore';
import { useRegistrationStore } from '../../stores/registrationStore';
import { useInclusionRequestStore } from '../../stores/inclusionRequestStore';
import { useAuthStore } from '../../stores/authStore';
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  ArrowLeftRight,
  ArrowUp,
  CalendarDays,
  Check,
  ShoppingCart,
  Info,
  Clock,
  CheckCircle2,
  Lock,
  X,
  Send,
} from 'lucide-react';
import clsx from 'clsx';
import { inscriptionsOuvertes, momentDOuverture } from '../../lib/seo/stage';
import toast, { Toaster } from 'react-hot-toast';
import ActivityModal, { ImageWithFallback } from '../../components/common/ActivityModal';
import { Link } from 'react-router-dom';
import { completudeEnfant, completudeProfil, resumeEnfant } from '../../lib/completude';
import { isFull } from '../../lib/places';
import { centreParDefaut, enfantSuivant } from '../../lib/catalogue';
import {
  accesDeLEnfant,
  cleDeSelection,
  etatInclusion,
  messageDeDemande,
  repartirSelection,
  type DemandeLue,
  type EtatInclusion,
} from '../../lib/inclusion/demandes';
import {
  aujourdHuiABruxelles,
  choisir,
  datesDeLaCarte,
  duAu,
  enTeteDeSemaine,
  etatDeLaCarte,
  etatDeLaSemaine,
  grouperParSemaine,
  messageDuRefus,
  nommer,
  occupantsDe,
  occupantsDesInscriptions,
  occupantsDesLignes,
  retirer,
  verifierAvantPanier,
  type Choix,
  type InscriptionDatee,
  type Occupant,
  type Periode,
  type SemaineDeStages,
  type SessionDatee,
} from '../../lib/inscriptions/semaines';
import { lireInscriptionsDatees, lireSessions } from '../../lib/inscriptions/semaines.gateway';
import DisponibilitePlaces from '../../components/common/DisponibilitePlaces';

/*
  La page des stages, un enfant à la fois.

  Avant : trois listes obligatoires (un enfant, un centre, une période) et rien
  à l'écran tant qu'elles n'étaient pas toutes remplies.

  Le centre est désormais celui de l'école de l'enfant, et la période la plus
  proche. L'enfant, lui, se CHOISIT dans une liste (décision Éclosion du
  24/10/2026) : la version du 09/10 cochait tous les enfants d'office et
  empilait leurs stages, alors qu'inscrire deux enfants au même stage est
  rare — le parent ne savait plus pour qui il cochait une semaine. Avec un
  seul enfant, il est choisi d'office ; avec plusieurs, rien ne s'affiche
  tant que le parent n'a pas dit lequel, sauf s'il arrive de la fiche d'un
  enfant (`state.nouvelEnfant`, `?enfant=`).

  Les stages sont rangés par semaine, et une semaine se prend UNE fois par
  enfant (VALIDÉ ÉCLOSION, 30/10/2026 ; `lib/inscriptions/semaines.ts`).
  Choisir un stage met les autres de la semaine en gris ; un choix en cours
  s'échange (« Choisir celui-ci à la place »), une inscription, une ligne du
  panier ou une demande d'inclusion en attente bloquent la semaine. Avant,
  rien n'empêchait deux stages la même semaine pour le même enfant : 122
  paires en production depuis 2025, la plupart annulées ensuite.

  Un choix est un couple « semaine × enfant » qui garde ses dates et sa
  session : changer d'enfant, de centre ou de période le garde, et
  « Ajouter au panier » l'emporte. Avant, l'ajout ne prenait que les choix
  affichés et laissait tomber les autres sans un mot.

  Après l'ajout au panier, un compte de plusieurs enfants se voit proposer
  le suivant, dans le même centre (VALIDÉ ÉCLOSION, 30/10/2026) ; un compte
  d'un seul enfant va au panier.

  Un enfant qui a des besoins particuliers passe d'abord par l'équipe (25-26/10/2026,
  `lib/inclusion/demandes.ts`) : ses semaines ouvertes se cochent comme les
  autres, puis partent en UNE demande ; chaque semaine dit ensuite où en est la
  sienne. Acceptée, l'équipe inscrit l'enfant et la facture part ; refusée, le
  parent reçoit la raison. Le niveau d'accès, décidé par l'équipe, dit la
  suite : 1, le chemin commun ; 2, une demande à chaque fois ; 3, ni
  inscription ni demande.
*/

/** Une case cochée : telle semaine pour tel enfant. */
const cle = cleDeSelection;

/** Un choix en cours : ses dates pour la règle, sa session pour le panier. */
interface ChoixRetenu extends Choix {
  activity: Activity;
}

const ageEnAnnees = (dateNaissance: string | null) => {
  if (!dateNaissance) return null;
  const n = new Date(dateNaissance);
  const a = new Date();
  let ans = a.getFullYear() - n.getFullYear();
  if (a.getMonth() < n.getMonth() || (a.getMonth() === n.getMonth() && a.getDate() < n.getDate())) ans--;
  return ans;
};

const etatDe = (kid: KidWithDetails) =>
  completudeEnfant({
    kids: kid,
    health: kid.health,
    allergies: kid.allergies,
    activities: kid.activities,
    departure: kid.departure,
    inclusion: kid.inclusion,
    image_rights: kid.image_rights,
  });

/** Ce que la règle sait d'une session du catalogue affiché. */
const sessionDatee = (a: Activity): SessionDatee => ({
  id: a.id,
  start_date: a.start_date,
  end_date: a.end_date,
  titre: a.stage.title,
  centre: a.center.name,
  type_activite: a.stage.type_activite ?? null,
  active: true,
  archived: false,
});

/**
 * Le numéro d'un critère : 1 l'enfant, 2 le centre, 3 la période.
 *
 * Les trois listes se ressemblaient, et le parent ne savait pas par laquelle
 * commencer — ni que l'enfant décide de tout le reste (âge, école, donc
 * centre et tarif). Le numéro dit l'ordre ; l'étape à faire est en couleur,
 * celles qui attendent sont grises.
 */
const NumeroCritere = ({ numero, etat }: { numero: number; etat: 'a-faire' | 'pret' | 'en-attente' }) => (
  <span
    aria-hidden="true"
    className={clsx(
      'mr-2 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
      etat === 'a-faire' && 'bg-primary-600 text-white ring-4 ring-primary-100',
      etat === 'pret' && 'bg-primary-100 text-primary-700',
      etat === 'en-attente' && 'bg-gray-100 text-gray-400',
    )}
  >
    {numero}
  </span>
);

/** Ce que la fiche enfant transmet en arrivant ici après un enregistrement. */
interface EtatArrivee {
  nouvelEnfant?: { id: string; prenom: string };
  photoManquee?: boolean;
}

const ActivitiesPage = () => {
  const navigate = useNavigate();
  const arrivee = (useLocation().state ?? {}) as EtatArrivee;
  const [params] = useSearchParams();
  const [bandeauVisible, setBandeauVisible] = useState(!!arrivee.nouvelEnfant);
  const { kids, fetchKids } = useKidStore();
  const { items: panier, addItem, removeItem } = useCartStore();
  const { profile } = useAuthStore();
  const { centers, periodes, fetchCenters, fetchPeriodes } = useActivityStore();
  const { addToWaitingList, isOnWaitingList, fetchWaitingList } = useWaitingListStore();
  const { fetchRegistrations, isKidRegistered } = useRegistrationStore();
  const { mesDemandes, chargerMesDemandes, envoyerDemande } = useInclusionRequestStore();

  /* `null` tant que les enfants ne sont pas chargés ; `''` : aucun choisi. */
  const [enfantId, setEnfantId] = useState<string | null>(null);
  const [centre, setCentre] = useState('');
  const [centreChoisi, setCentreChoisi] = useState(false);
  /* Le prénom de l'enfant dont on a repris le centre (« Le même centre que pour Léa »). */
  const [centreRepris, setCentreRepris] = useState<string | null>(null);
  const [periode, setPeriode] = useState('');

  const [parEnfant, setParEnfant] = useState<Record<string, Activity[]>>({});
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const requete = useRef(0);

  const [choix, setChoix] = useState<ChoixRetenu[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [joiningWaitingList, setJoiningWaitingList] = useState<string | null>(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [ajoutEnCours, setAjoutEnCours] = useState(false);
  /* Relit les stages affichés : après un refus, un stage complet s'affiche complet. */
  const [relecture, setRelecture] = useState(0);

  /* Ce qui prend déjà une semaine : les inscriptions datées, et les sessions du panier et des demandes. */
  const [inscriptionsDatees, setInscriptionsDatees] = useState<InscriptionDatee[]>([]);
  const [inscriptionsIllisibles, setInscriptionsIllisibles] = useState(false);
  const [sessionsLues, setSessionsLues] = useState<Map<string, SessionDatee>>(new Map());
  const sessionsDemandees = useRef(new Set<string>());

  /* La question après le panier, et les enfants déjà faits pendant cette visite. */
  const [proposition, setProposition] = useState<{ suivant: KidWithDetails; faits: string[] } | null>(null);
  const [enfantsFaits, setEnfantsFaits] = useState<string[]>([]);
  const focusSurEnfant = useRef(false);

  /* Ce qui vient de changer, dit aux lecteurs d'écran. */
  const [annonce, setAnnonce] = useState('');

  const aujourdHui = useMemo(() => aujourdHuiABruxelles(), []);

  useEffect(() => {
    fetchKids();
    fetchCenters();
    fetchPeriodes();
    fetchWaitingList();
    fetchRegistrations();
    chargerMesDemandes();
  }, [fetchKids, fetchCenters, fetchPeriodes, fetchWaitingList, fetchRegistrations, chargerMesDemandes]);

  /* --- Les choix par défaut -------------------------------------------- */

  /*
    Au premier chargement : l'enfant qu'on vient d'enregistrer ou de vérifier
    (c'est pour lui que le parent arrive), sinon le seul enfant du compte,
    sinon personne — le parent choisit.
  */
  const demande = arrivee.nouvelEnfant?.id ?? params.get('enfant');
  useEffect(() => {
    if (enfantId !== null || kids.length === 0) return;
    if (demande && kids.some((k) => k.id === demande)) setEnfantId(demande);
    else setEnfantId(kids.length === 1 ? kids[0].id : '');
  }, [kids, enfantId, demande]);

  const enfant = useMemo(() => kids.find((k) => k.id === enfantId) ?? null, [kids, enfantId]);
  /* La page charge et affiche « par enfant » : une liste d'un enfant, ou vide. */
  const coches = useMemo(() => (enfant ? [enfant] : []), [enfant]);

  /*
    Le centre relié à son école, tant que le parent n'en a pas choisi un
    lui-même — ni repris celui de l'enfant précédent (« Oui, inscrire Tom »).
    Un enfant choisi ensuite dans la liste retrouve le centre de son école.
  */
  useEffect(() => {
    if (!centreChoisi && !centreRepris) setCentre(centreParDefaut(coches, centers));
  }, [coches, centers, centreChoisi, centreRepris]);

  // La période la plus proche (la liste arrive déjà dans l'ordre des dates)
  useEffect(() => {
    if (!periode && periodes.length > 0) setPeriode(periodes[0]);
  }, [periodes, periode]);

  /* --- Les stages, enfant par enfant ------------------------------------ */

  useEffect(() => {
    if (coches.length === 0 || !periode) {
      setParEnfant({});
      return;
    }
    const numero = ++requete.current;
    setChargement(true);
    setErreur(null);

    Promise.all(
      coches.map(async (k) => {
        const sessions = await chargerSessions({
          kid_id: k.id,
          center_id: centre || undefined,
          periode,
        });
        return [k.id, sessions] as const;
      }),
    )
      .then((resultats) => {
        if (numero === requete.current) setParEnfant(Object.fromEntries(resultats));
      })
      .catch((e: unknown) => {
        if (numero === requete.current) {
          setErreur(e instanceof Error ? e.message : 'Une erreur est survenue lors du chargement des stages.');
        }
      })
      .finally(() => {
        if (numero === requete.current) setChargement(false);
      });
  }, [coches, centre, periode, relecture]);

  /* --- Ce qui prend déjà une semaine ----------------------------------- */

  const idsDesEnfants = useMemo(() => kids.map((k) => k.id), [kids]);
  const desEnfants = useMemo(() => new Set(idsDesEnfants), [idsDesEnfants]);

  /*
    Les inscriptions de la famille, avec les dates de leur session : une
    inscription dans un autre centre ou une autre période prend aussi la
    semaine. Une lecture qui échoue ne libère rien en silence : la page le dit,
    et l'ajout au panier relit avant d'agir.
  */
  useEffect(() => {
    if (idsDesEnfants.length === 0) return;
    let vivant = true;
    lireInscriptionsDatees(idsDesEnfants)
      .then((lues) => {
        if (!vivant) return;
        setInscriptionsDatees(lues);
        setInscriptionsIllisibles(false);
      })
      .catch((e: unknown) => {
        console.error('Error fetching dated registrations:', e);
        if (vivant) setInscriptionsIllisibles(true);
      });
    return () => {
      vivant = false;
    };
  }, [idsDesEnfants]);

  /* Les demandes d'inclusion en attente prennent la semaine (VALIDÉ ÉCLOSION, 30/10/2026). */
  const demandesEnAttente = useMemo(
    () =>
      mesDemandes.filter(
        (d) =>
          d.status === 'pending' &&
          accesDeLEnfant(kids.find((k) => k.id === d.kid_id)?.inclusion) !== 'impossible',
      ),
    [mesDemandes, kids],
  );
  const lignesDuPanier = useMemo(() => panier.filter((l) => desEnfants.has(l.kid_id)), [panier, desEnfants]);

  /* Les sessions connues : celles affichées, et celles relues pour le panier et les demandes. */
  const sessionsConnues = useMemo(() => {
    const connues = new Map(sessionsLues);
    for (const liste of Object.values(parEnfant)) for (const a of liste) connues.set(a.id, sessionDatee(a));
    for (const c of choix) connues.set(c.sessionId, sessionDatee(c.activity));
    return connues;
  }, [sessionsLues, parEnfant, choix]);

  /* Le panier ne porte pas de dates : on relit, une fois, les sessions qu'on ne connaît pas. */
  const aRelire = useMemo(
    () =>
      [...new Set([...lignesDuPanier, ...demandesEnAttente].map((l) => l.activity_id))].filter(
        (id) => !sessionsConnues.has(id) && !sessionsDemandees.current.has(id),
      ),
    [lignesDuPanier, demandesEnAttente, sessionsConnues],
  );
  useEffect(() => {
    if (aRelire.length === 0) return;
    for (const id of aRelire) sessionsDemandees.current.add(id);
    lireSessions(aRelire)
      .then((lues) => setSessionsLues((avant) => new Map([...avant, ...lues])))
      .catch((e: unknown) => console.error('Error fetching cart sessions:', e));
  }, [aRelire]);

  const occupantsBloquants = useMemo<Occupant[]>(
    () => [
      ...occupantsDesInscriptions(inscriptionsDatees),
      ...occupantsDesLignes('panier', lignesDuPanier, sessionsConnues),
      ...occupantsDesLignes('demande', demandesEnAttente, sessionsConnues),
    ],
    [inscriptionsDatees, lignesDuPanier, demandesEnAttente, sessionsConnues],
  );
  const occupants = useMemo<Occupant[]>(
    () => [
      ...occupantsBloquants,
      ...choix.map((c) => ({
        source: 'choix' as const,
        sessionId: c.sessionId,
        kidId: c.kidId,
        start_date: c.start_date,
        end_date: c.end_date,
        titre: c.activity.stage.title,
        centre: c.activity.center.name,
      })),
    ],
    [occupantsBloquants, choix],
  );

  /*
    Un choix dont la semaine est prise entre-temps (le panier relu, une
    inscription arrivée d'un autre onglet) sort des choix, et on le dit : il
    ne resterait pas affiché comme valable.
  */
  useEffect(() => {
    const perimes = choix.filter((c) => occupantsDe(c, c.kidId, occupantsBloquants).length > 0);
    if (perimes.length === 0) return;
    setChoix((cs) => cs.filter((c) => !perimes.some((p) => p.cle === c.cle)));
    for (const p of perimes) {
      const prenom = kids.find((k) => k.id === p.kidId)?.prenom ?? 'votre enfant';
      const par = occupantsDe(p, p.kidId, occupantsBloquants);
      /*
        Le même stage, déjà au panier ou déjà demandé : le choix vient d'y
        partir (le magasin se met à jour avant la page), rien à dire.
      */
      if (par.every((o) => o.sessionId === p.sessionId && (o.source === 'panier' || o.source === 'demande'))) continue;
      toast(
        par.some((o) => o.sessionId === p.sessionId)
          ? `Déjà pris pour ${prenom}, retiré de vos choix : ${p.activity.stage.title}.`
          : `${prenom} a déjà un stage la semaine ${duAu(p.start_date, p.end_date, aujourdHui)} (${nommer(par, null)}). Retiré de vos choix : ${p.activity.stage.title}.`,
        { duration: 7000 },
      );
    }
  }, [choix, occupantsBloquants, kids, aujourdHui]);

  /* --- Actions ----------------------------------------------------------- */

  const selection = useMemo(() => choix.map((c) => c.cle), [choix]);

  /* Les semaines déjà choisies, par prénom : le bouton du panier dit pour qui. */
  const choisiesParEnfant = useMemo(() => {
    const compte = new Map<string, number>();
    for (const c of choix) compte.set(c.kidId, (compte.get(c.kidId) ?? 0) + 1);
    return [...compte.entries()].map(([kidId, n]) => ({
      prenom: kids.find((k) => k.id === kidId)?.prenom ?? '',
      n,
    }));
  }, [choix, kids]);

  /*
    Cocher, décocher, ou prendre ce stage à la place de celui de la semaine :
    `choisir` retire de lui-même l'autre choix de l'enfant qui recoupe celui-ci.
  */
  const basculer = (activity: Activity, kid: KidWithDetails) => {
    const c = cle(activity.id, kid.id);
    const titre = activity.stage.title;
    if (selection.includes(c)) {
      setChoix((cs) => retirer(cs, c));
      setAnnonce(`Stage retiré pour ${kid.prenom} : ${titre}.`);
      return;
    }
    const { choix: suite, retires } = choisir(choix, {
      cle: c,
      sessionId: activity.id,
      kidId: kid.id,
      start_date: activity.start_date,
      end_date: activity.end_date,
      activity,
    });
    setChoix(suite);
    if (retires.length === 0) {
      setAnnonce(
        `Stage choisi pour ${kid.prenom} : ${titre}. Les autres stages de cette semaine ne se cochent plus ; « Choisir celui-ci à la place » permet de changer.`,
      );
      return;
    }
    const anciens = retires.map((r) => ({ titre: r.activity.stage.title, centre: r.activity.center.name }));
    setAnnonce(`Stage choisi pour ${kid.prenom} : ${titre} (au lieu de : ${nommer(anciens, activity.center.name)}).`);
    /*
      L'ancien choix n'est pas à l'écran (autre centre, autre période) : on le
      montre aussi. Le toast se tait pour les lecteurs d'écran : l'annonce
      ci-dessus le dit déjà.
    */
    const affichees = new Set((parEnfant[kid.id] ?? []).map((a) => a.id));
    const caches = retires.filter((r) => !affichees.has(r.sessionId));
    if (caches.length > 0) {
      toast(`Stage choisi pour ${kid.prenom} : ${titre}, au lieu de ${nommer(caches.map((r) => ({ titre: r.activity.stage.title, centre: r.activity.center.name })), null)}.`, {
        duration: 7000,
        ariaProps: { role: 'status', 'aria-live': 'off' },
      });
    }
  };

  /* Après un geste qui fait disparaître le bouton touché, le focus va là où l'on continue. */
  const focaliser = (id: string) => requestAnimationFrame(() => document.getElementById(id)?.focus());

  const retirerDuPanier = (o: Occupant) => {
    const ligne = panier.find((l) => l.activity_id === o.sessionId && l.kid_id === o.kidId);
    if (!ligne) return;
    removeItem(ligne.id);
    const prenom = kids.find((k) => k.id === o.kidId)?.prenom ?? 'votre enfant';
    toast.success(`Retiré de votre panier : ${o.titre}. Vous pouvez choisir un autre stage pour ${prenom} cette semaine.`, {
      duration: 6000,
    });
  };

  /* Les demandes du parent, par case : l'historique peut en porter plusieurs. */
  const demandesParCase = useMemo(() => {
    const parCase = new Map<string, DemandeLue[]>();
    for (const d of mesDemandes) {
      const c = cle(d.activity_id, d.kid_id);
      parCase.set(c, [...(parCase.get(c) ?? []), d]);
    }
    return parCase;
  }, [mesDemandes]);

  const etatPour = (activityId: string, kidId: string): EtatInclusion =>
    etatInclusion(kids.find((k) => k.id === kidId)?.inclusion, demandesParCase.get(cle(activityId, kidId)) ?? []);

  const repartition = repartirSelection(selection, etatPour);
  const aDemanderParEnfant = [...repartition.demandes.entries()].map(([kidId, semaines]) => ({
    prenom: kids.find((k) => k.id === kidId)?.prenom ?? '',
    n: semaines.length,
  }));

  /*
    Une demande par enfant, pour toutes ses semaines cochées : la fonction Edge
    écarte ce qui est déjà demandé et ne prévient l'équipe qu'une fois. Rend
    `false` si une demande n'est pas partie.
  */
  const envoyerLesDemandes = async (): Promise<boolean> => {
    let toutesParties = true;
    setEnvoiEnCours(true);
    try {
      for (const [kidId, sessionIds] of repartition.demandes) {
        const prenom = kids.find((k) => k.id === kidId)?.prenom || 'votre enfant';
        try {
          const resultat = await envoyerDemande(kidId, sessionIds);
          const message = messageDeDemande(prenom, resultat);
          if (message.ton === 'succes') toast.success(message.texte, { duration: 7000 });
          else toast(message.texte, { duration: 7000 });
          if (resultat.statut === 'traitee') {
            setChoix((cs) => cs.filter((c) => !sessionIds.some((a) => c.cle === cle(a, kidId))));
          } else {
            // L'équipe a changé la fiche entre-temps : on la relit, la semaine va au panier.
            await fetchKids();
          }
        } catch (e) {
          toutesParties = false;
          console.error('Error sending inclusion request:', e);
          toast.error(e instanceof Error && e.message ? e.message : "La demande n'a pas pu partir. Réessayez dans un instant.");
        }
      }
    } finally {
      setEnvoiEnCours(false);
    }
    return toutesParties;
  };

  const handleAddToCart = async () => {
    /*
      Les semaines à demander partent avant de quitter la page : elles ne se
      perdent pas. Si une demande échoue, on reste ici — l'erreur se lit, et
      le parent peut réessayer.
    */
    if (repartition.demandes.size > 0 && !(await envoyerLesDemandes())) return;
    const aAjouter = choix.filter((c) => repartition.panier.includes(c.cle));
    if (aAjouter.length === 0) return;

    /*
      Tous les choix partent, quel que soit l'enfant, le centre ou la période
      affichés. Avant, seuls les choix de l'écran courant partaient : les
      autres tombaient sans un mot. On relit d'abord le panier (un autre
      onglet a pu le changer), les inscriptions et les sessions choisies : un
      stage complet, fermé, ou dont la semaine a été prise entre-temps, ne part
      pas — et le parent sait lequel et pourquoi.
    */
    setAjoutEnCours(true);
    let relues: Awaited<ReturnType<typeof lireSessions>>;
    let inscriptions: InscriptionDatee[];
    let panierRelu: typeof panier;
    try {
      await useCartStore.persist.rehydrate();
      panierRelu = useCartStore.getState().items.filter((l) => desEnfants.has(l.kid_id));
      [relues, inscriptions] = await Promise.all([
        /*
          Les choix, ET les lignes du panier et les demandes : une ligne ajoutée
          dans un autre onglet n'a pas encore de session connue ici, et une
          ligne sans session ne bloquerait rien.
        */
        lireSessions([...aAjouter.map((c) => c.sessionId), ...[...panierRelu, ...demandesEnAttente].map((l) => l.activity_id)]),
        lireInscriptionsDatees(idsDesEnfants),
      ]);
    } catch (e) {
      console.error('Error checking choices before cart:', e);
      toast.error("Nous n'avons pas pu vérifier vos choix. Réessayez dans un instant.");
      setAjoutEnCours(false);
      return;
    }
    setInscriptionsDatees(inscriptions);
    setInscriptionsIllisibles(false);
    setSessionsLues((avant) => new Map([...avant, ...relues]));

    const sessions = new Map<string, SessionDatee>([...sessionsConnues, ...relues]);
    const bloquants = [
      ...occupantsDesInscriptions(inscriptions),
      ...occupantsDesLignes('panier', panierRelu, sessions),
      ...occupantsDesLignes('demande', demandesEnAttente, sessions),
    ];
    const { ajoutes, refus } = verifierAvantPanier(aAjouter, relues, bloquants, new Date());

    /*
      Tout choix examiné sort des choix : ajouté, il est au panier ; refusé, le
      message le nomme et sa semaine redevient libre pour un autre stage. Un
      choix refusé gardé coché redonnerait le même refus à chaque clic.
    */
    setChoix((cs) => cs.filter((c) => !aAjouter.some((a) => a.cle === c.cle)));
    for (const c of ajoutes) {
      const kid = kids.find((k) => k.id === c.kidId);
      const activity = c.activity;
      if (!kid) continue;
      /*
        Aucun prix n'entre dans le panier : celui qui sera facturé est résolu en
        base (`quote_order` pour l'affichage, `create_order` pour l'écriture).
      */
      addItem({
        id: `${activity.id}-${kid.id}`,
        activity_id: activity.id,
        kid_id: kid.id,
        kidName: `${kid.prenom} ${kid.nom}`,
        activityName: activity.stage.title,
        activityCategory: activity.periode ?? 'Stage',
        dateRange: `${new Date(activity.start_date).toLocaleDateString('fr-FR')} - ${new Date(activity.end_date).toLocaleDateString('fr-FR')}`,
        imageUrl: activity.stage.image_url ?? undefined,
      });
    }
    setAjoutEnCours(false);

    /*
      Un refus : on reste sur la page, qui relit ses stages (le complet
      s'affiche complet), pour que le parent choisisse un autre stage cette
      semaine. Ce qui est parti est au panier.
    */
    if (refus.length > 0) {
      const lignes = refus.map((r) =>
        messageDuRefus(
          r.choix.activity.stage.title,
          kids.find((k) => k.id === r.choix.kidId)?.prenom ?? 'votre enfant',
          r,
          aujourdHui,
        ),
      );
      const debut = ajoutes.length > 0
        ? `${ajoutes.length} stage${ajoutes.length > 1 ? 's' : ''} ajouté${ajoutes.length > 1 ? 's' : ''} au panier. `
        : '';
      toast.error(`${debut}${lignes.join(' ')}`, { duration: 12000 });
      setRelecture((n) => n + 1);
      return;
    }

    /*
      Plusieurs enfants : on propose le suivant, dans le même centre et la même
      période ; un seul, ou plus personne à inscrire : le panier.
    */
    const faits = [...new Set([...enfantsFaits, ...ajoutes.map((c) => c.kidId), ...(enfantId ? [enfantId] : [])])];
    setEnfantsFaits(faits);
    const suivant = kids.length > 1
      ? enfantSuivant(kids, enfantId, new Set(faits), (k) => accesDeLEnfant(k.inclusion) === 'impossible')
      : null;
    if (suivant) setProposition({ suivant, faits: [...new Set(ajoutes.map((c) => c.kidId))] });
    else navigate('/cart');
  };

  /* « Oui, inscrire Tom » : même centre, même période, et on remonte en haut de la page. */
  const inscrireLeSuivant = () => {
    if (!proposition) return;
    setCentreRepris(enfant?.prenom ?? null);
    setEnfantId(proposition.suivant.id);
    setAnnonce(`Les stages pour ${proposition.suivant.prenom}.`);
    setProposition(null);
    focusSurEnfant.current = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* Après « Oui, inscrire Tom », le focus va au titre de ses stages, une fois chargés. */
  useEffect(() => {
    if (!focusSurEnfant.current || chargement || !enfant || !parEnfant[enfant.id]) return;
    focusSurEnfant.current = false;
    document.getElementById(`stages-${enfant.id}`)?.focus();
  }, [chargement, enfant, parEnfant]);

  /*
    L'élément focalisé au clavier ne doit passer ni sous la barre de navigation
    et l'en-tête collant de la semaine, ni sous les boutons fixes du panier :
    une marge de défilement sur la page, le temps qu'elle est affichée.
  */
  useEffect(() => {
    const html = document.documentElement;
    html.style.scrollPaddingTop = '13.5rem';
    html.style.scrollPaddingBottom = '10rem';
    return () => {
      html.style.scrollPaddingTop = '';
      html.style.scrollPaddingBottom = '';
    };
  }, []);

  const handleJoinWaitingList = async (activityId: string, kidId: string) => {
    try {
      setJoiningWaitingList(cle(activityId, kidId));
      await addToWaitingList(activityId, kidId);
      toast.success("Vous avez été ajouté à la liste d'attente");
    } catch (error) {
      console.error('Error joining waiting list:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setJoiningWaitingList(null);
    }
  };

  /*
    Le profil du parent se vérifie AVANT les stages, plus après le panier : le
    refus arrivait sinon à l'instant où il coûtait le plus cher.
  */
  const etatProfil = completudeProfil(profile);

  /* --- Rendu ------------------------------------------------------------- */

  /** Ce qu'une session permet à un enfant, avant la règle de la semaine. */
  const situation = (activity: Activity, kid: KidWithDetails) => {
    const isSessionFull = isFull(activity);
    const isAlreadyRegistered = isKidRegistered(kid.id, activity.id);
    const dansLePanier = lignesDuPanier.some((l) => l.activity_id === activity.id && l.kid_id === kid.id);
    const inclusion = etatPour(activity.id, kid.id);
    /*
      Les stages à venir sont montrés avec la date d'ouverture de leurs
      inscriptions ; les prendre reste impossible (déclencheur en base). Une
      demande d'inclusion non plus : ni demande ni inscription avant
      l'ouverture (VALIDÉ ÉCLOSION, 26/10/2026 ; `demander_inclusion` l'écarte).
    */
    const ouvert = inscriptionsOuvertes(activity);
    const cochableSansRegle =
      ouvert &&
      !isSessionFull &&
      !isAlreadyRegistered &&
      !dansLePanier &&
      (inclusion.etat === 'libre' || inclusion.etat === 'a-demander');
    return { isSessionFull, isAlreadyRegistered, dansLePanier, inclusion, ouvert, cochableSansRegle };
  };

  const carte = (activity: Activity, kid: KidWithDetails, semaine: Periode, Titre: 'h4' | 'h5' = 'h4') => {
    const { isSessionFull, isAlreadyRegistered, dansLePanier, inclusion, ouvert, cochableSansRegle } = situation(activity, kid);
    const isWaiting = isOnWaitingList(activity.id, kid.id);
    const c = cle(activity.id, kid.id);
    const idCase = `case-${c}`;
    const idInfos = `infos-${c}`;
    const choisie = selection.includes(c);
    const regle = etatDeLaCarte(activity, kid.id, cochableSansRegle, choisie, occupants);
    /* Pendant l'envoi ou la vérification, les choix ne bougent pas : ce qui part est ce qui est coché. */
    const occupe = ajoutEnCours || envoiEnCours;
    const cochable = regle.etat === 'libre' || regle.etat === 'choisie';
    const grise = regle.etat === 'remplacable' || regle.etat === 'bloquee';
    const pris = grise ? regle.par : [];
    /* Une seule pastille sur l'image, la plus utile : elle ne se superpose plus à une autre. */
    const badge = isAlreadyRegistered
      ? { texte: 'Déjà inscrit·e', classe: 'bg-green-50 text-green-800 ring-green-300' }
      : dansLePanier
        ? { texte: 'Dans votre panier', classe: 'bg-primary-50 text-primary-800 ring-primary-300' }
        : !ouvert
          ? { texte: 'Bientôt', classe: 'bg-amber-50 text-amber-800 ring-amber-300' }
          : isSessionFull
            ? { texte: 'Complet', classe: 'bg-red-50 text-red-800 ring-red-300' }
            : null;
    const badgeInclusion = isAlreadyRegistered
      ? null
      : inclusion.etat === 'demandee'
        ? { texte: 'Demande envoyée', classe: 'bg-blue-600' }
        : inclusion.etat === 'refusee'
          ? { texte: 'Non retenue', classe: 'bg-gray-500' }
          : inclusion.etat === 'a-demander' && !isSessionFull
            ? { texte: 'Sur demande', classe: 'bg-blue-500' }
            : inclusion.etat === 'libre' && inclusion.acceptee
              ? { texte: 'Demande acceptée', classe: 'bg-green-600' }
              : null;
    /* La liste d'attente reste possible même si l'enfant a déjà un stage cette semaine (VALIDÉ ÉCLOSION, 30/10/2026). */
    const listeDAttente =
      isSessionFull &&
      !isAlreadyRegistered &&
      inclusion.etat !== 'demandee' &&
      inclusion.etat !== 'refusee' &&
      inclusion.etat !== 'impossible';
    const semainePrise = occupantsDe(activity, kid.id, occupants, activity.id).length > 0;
    const dates = datesDeLaCarte(activity, semaine);

    const contenu = (
      <>
        <div className="relative">
          <ImageWithFallback
            src={activity.stage.image_url}
            alt={activity.stage.title}
            className={clsx('w-full h-48 object-cover', grise && 'opacity-50')}
          />
          {/* La case : carrée, comme sur les journées pédagogiques. */}
          {cochable && (
            <span
              aria-hidden="true"
              className={clsx(
                'absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-lg border-2 shadow-sm',
                choisie ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-400 bg-white',
              )}
            >
              {choisie && <Check className="h-4 w-4" strokeWidth={3} />}
            </span>
          )}
          {badge && (
            <div className="absolute top-4 left-4">
              <span className={clsx('rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset', badge.classe)}>
                {badge.texte}
              </span>
            </div>
          )}
          {/* En bas de l'image : la pastille d'état garde le coin du haut. */}
          {badgeInclusion && (
            <div className="absolute bottom-3 left-4">
              <span className={clsx('text-white text-xs font-bold px-2 py-1 rounded-full', badgeInclusion.classe)}>
                {badgeInclusion.texte}
              </span>
            </div>
          )}
        </div>

        <div className="p-6">
          <Titre className={clsx('text-xl font-semibold mb-2', grise ? 'text-gray-700' : 'text-gray-900')}>
            {activity.stage.title}
          </Titre>

          <div id={idInfos} className="space-y-2 text-sm text-gray-600">
            {/* Les dates de la semaine sont dans son en-tête ; seules celles qui diffèrent sont redites. */}
            {dates && <p className="font-medium text-gray-700">{dates}</p>}
            <p>{activity.center.name}</p>
            <p className={clsx('font-semibold', grise ? 'text-gray-600' : 'text-primary-600')}>
              {activity.calculated_main_price ?? activity.prix_normal} €
              {(activity.calculated_reduced_price ?? activity.prix_reduit) && (
                <span className="text-sm text-gray-500 ml-2">
                  (Réduit: {activity.calculated_reduced_price ?? activity.prix_reduit} €)
                </span>
              )}
            </p>
            {activity.calculated_main_price !== activity.prix_normal && (
              <span className="inline-block text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Tarif local appliqué
              </span>
            )}
            {ouvert ? (
              <DisponibilitePlaces session={activity} />
            ) : (
              <p className="text-sm font-medium text-amber-700">
                Ouverture des inscriptions {momentDOuverture(activity.visible_from ?? '')}.
              </p>
            )}

            {regle.etat === 'remplacable' ? (
              <p className="mt-2 text-sm text-gray-700">
                {kid.prenom} a déjà un stage cette semaine&nbsp;: {nommer(pris, activity.center.name)}.
              </p>
            ) : regle.etat === 'bloquee' ? (
              <p className="mt-2 text-sm text-gray-700">
                {pris[0]?.source === 'inscription'
                  ? pris.length === 1 && pris[0].annulationDemandee
                    ? `${kid.prenom} a encore sa place cette semaine : ${nommer(pris, activity.center.name)} (annulation demandée).`
                    : `${kid.prenom} a déjà sa place cette semaine : ${nommer(pris, activity.center.name)}.`
                  : pris[0]?.source === 'panier'
                    ? `Dans votre panier pour ${kid.prenom} cette semaine : ${nommer(pris, activity.center.name)}.`
                    : `Demande envoyée pour ${kid.prenom} cette semaine : ${nommer(pris, activity.center.name)}.`}
              </p>
            ) : isAlreadyRegistered ? (
              <div className="mt-2 flex items-center text-green-700 text-sm">
                <Clock className="h-4 w-4 mr-1" />
                <span>Déjà inscrit</span>
              </div>
            ) : inclusion.etat === 'demandee' ? (
              <div className="mt-2 flex items-start text-blue-700 text-sm">
                <Clock className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                <span>
                  Demande envoyée le {new Date(inclusion.le).toLocaleDateString('fr-FR')}. L’équipe vérifie
                  l’accueil et vous répond par courriel.
                </span>
              </div>
            ) : inclusion.etat === 'refusee' ? (
              <p className="mt-2 text-sm text-gray-600">
                Demande non retenue pour cette semaine&nbsp;: nous vous avons écrit pourquoi. Pour en parler&nbsp;:{' '}
                <a href="mailto:info@eclosion.be" className="font-medium text-primary-700 underline">
                  info@eclosion.be
                </a>
              </p>
            ) : inclusion.etat === 'impossible' ? (
              <p className="mt-2 text-sm text-gray-600">
                Pas d’inscription possible pour {kid.prenom}&nbsp;: voir le message en haut de la page.
              </p>
            ) : inclusion.etat === 'a-demander' && !isSessionFull ? (
              <p className="mt-2 text-sm text-blue-700">
                {!ouvert
                  ? 'Sur demande, dès l’ouverture des inscriptions.'
                  : choisie ? 'Cochée pour la demande.' : 'Sur demande : cochez-la, puis « Envoyer la demande ».'}
              </p>
            ) : inclusion.etat === 'libre' && inclusion.acceptee && !isSessionFull ? (
              <div className="mt-2 flex items-center text-green-700 text-sm">
                <CheckCircle2 className="h-4 w-4 mr-1 flex-shrink-0" />
                <span>
                  L’équipe a accepté&nbsp;: {ouvert ? 'vous pouvez l’inscrire.' : 'vous pourrez l’inscrire dès l’ouverture.'}
                </span>
              </div>
            ) : listeDAttente && semainePrise && !isWaiting ? (
              <p className="mt-2 text-sm text-gray-600">Si une place se libère ici, nous vous écrivons.</p>
            ) : null}
          </div>
        </div>
      </>
    );

    return (
      <article
        key={c}
        className={clsx(
          'overflow-hidden rounded-xl transition-colors',
          'has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-primary-300',
          regle.etat === 'choisie'
            ? 'bg-primary-50 shadow-md ring-2 ring-primary-600'
            : grise
              ? 'bg-gray-50 ring-1 ring-gray-200'
              : 'bg-white shadow-md',
        )}
      >
        {/*
          Toute la carte est la case quand elle se coche — le motif des
          journées pédagogiques. Les boutons (détails, liste d'attente,
          échange) sont sous la carte, hors de la case.
        */}
        {cochable ? (
          <label className={clsx('block', occupe ? 'cursor-wait' : 'cursor-pointer')}>
            <input
              id={idCase}
              type="checkbox"
              className="sr-only"
              checked={choisie}
              disabled={occupe}
              onChange={() => basculer(activity, kid)}
              aria-label={`${activity.stage.title}, ${activity.center.name}, pour ${kid.prenom}`}
              aria-describedby={idInfos}
            />
            {contenu}
          </label>
        ) : (
          <div>{contenu}</div>
        )}

        <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-gray-200/70 px-4 py-1.5">
          <button
            type="button"
            onClick={() => {
              setSelectedActivity(activity);
              setIsModalOpen(true);
            }}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <Info className="h-4 w-4" aria-hidden="true" />
            Détails
            <span className="sr-only"> de {activity.stage.title}</span>
          </button>

          {regle.etat === 'choisie' ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
              <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
              {inclusion.etat === 'a-demander' ? 'Choisi pour la demande' : `Choisi pour ${kid.prenom}`}
            </span>
          ) : regle.etat === 'remplacable' ? (
            <button
              type="button"
              disabled={occupe}
              onClick={() => {
                basculer(activity, kid);
                // Le bouton disparaît : le focus va à la case de la carte, désormais choisie.
                focaliser(idCase);
              }}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-primary-600 bg-white px-3 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:cursor-wait disabled:opacity-50"
            >
              <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
              Choisir celui-ci à la place
              <span className="sr-only">
                {' '}: {activity.stage.title} pour {kid.prenom} (déjà choisi cette semaine : {nommer(pris, activity.center.name)})
              </span>
            </button>
          ) : regle.etat === 'bloquee' ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600">
              <Lock className="h-4 w-4" aria-hidden="true" />
              Semaine déjà prise
            </span>
          ) : listeDAttente ? (
            isWaiting ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-800">
                <Clock className="h-4 w-4" aria-hidden="true" />
                Sur liste d'attente
              </span>
            ) : (
              <button
                type="button"
                onClick={() => handleJoinWaitingList(activity.id, kid.id)}
                disabled={joiningWaitingList === c}
                className="btn-outline min-h-[44px] py-1 px-3 text-sm"
              >
                {joiningWaitingList === c ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    En cours...
                  </span>
                ) : (
                  "Rejoindre la liste d'attente"
                )}
              </button>
            )
          ) : null}
        </div>
      </article>
    );
  };

  /*
    Une semaine : son en-tête reste collé sous la barre de navigation pendant
    qu'on fait défiler ses cartes, et dit, une fois pour toutes, où en est la
    semaine pour l'enfant — et pourquoi ses cartes sont grises.
  */
  const semaineDeStages = (bloc: SemaineDeStages<Activity>, kid: KidWithDetails) => {
    const compte = { cochables: 0, bientot: 0, complets: 0 };
    for (const a of bloc.sessions) {
      const s = situation(a, kid);
      if (s.cochableSansRegle) compte.cochables++;
      else if (!s.ouvert) compte.bientot++;
      else if (s.isSessionFull) compte.complets++;
    }
    const centreAffiche = centre ? centers.find((x) => x.id === centre)?.name ?? null : null;
    const etat = etatDeLaSemaine(bloc, kid.id, kid.prenom, occupants, compte, centreAffiche);
    const idTitre = `semaine-${kid.id}-${bloc.cle}`;
    /*
      « Tous les centres » : les stages de la semaine rangés par centre, celui
      de l'école de l'enfant d'abord, puis par nom. L'ordre de la semaine
      (plaines, stages, titre) est gardé dans chaque centre.
    */
    const centreDeLEcole = centreParDefaut([kid], centers);
    const parCentre = centre !== '' ? null : (() => {
      const groupes = new Map<string, { id: string; nom: string; sessions: Activity[] }>();
      for (const a of bloc.sessions) {
        const g = groupes.get(a.center.id) ?? { id: a.center.id, nom: a.center.name, sessions: [] };
        g.sessions.push(a);
        groupes.set(a.center.id, g);
      }
      return [...groupes.values()].sort(
        (x, y) => Number(y.id === centreDeLEcole) - Number(x.id === centreDeLEcole) || x.nom.localeCompare(y.nom, 'fr'),
      );
    })();

    return (
      <section key={bloc.cle} aria-labelledby={idTitre} className="space-y-4">
        <div className="sticky top-16 z-20 -mx-4 border-y border-gray-200 bg-white/95 px-4 py-3 backdrop-blur-sm lg:mx-0 lg:rounded-xl lg:border">
          <div className="flex items-baseline justify-between gap-3">
            <h3 id={idTitre} tabIndex={-1} className="text-base font-semibold text-gray-900 md:text-lg">
              {enTeteDeSemaine(bloc, aujourdHui)}
            </h3>
            {bloc.jours !== null && bloc.jours < 5 && (
              <span className="flex-shrink-0 text-sm text-gray-600">{bloc.jours} jours</span>
            )}
          </div>
          <div
            className={clsx(
              'mt-1 flex flex-wrap items-center justify-between gap-x-3 text-sm',
              etat.ton === 'libre' || etat.ton === 'attente' ? 'text-gray-600' : 'font-medium',
              etat.ton === 'choix' && 'text-primary-700',
              etat.ton === 'panier' && 'text-primary-800',
              etat.ton === 'inscription' && 'text-green-800',
              etat.ton === 'demande' && 'text-blue-800',
            )}
          >
            <p className="flex min-w-0 items-start gap-1.5 py-1">
              {etat.ton === 'choix' && <Check className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={3} aria-hidden="true" />}
              {etat.ton === 'panier' && <ShoppingCart className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
              {etat.ton === 'inscription' && <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
              {etat.ton === 'demande' && <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
              <span>{etat.texte}</span>
            </p>
            {etat.ton === 'choix' && (
              <button
                type="button"
                onClick={() => {
                  setChoix((cs) => retirer(cs, cle(etat.occupant.sessionId, kid.id)));
                  setAnnonce(`Stage retiré pour ${kid.prenom} : ${etat.occupant.titre}.`);
                  focaliser(idTitre);
                }}
                aria-label={`Retirer le choix de ${kid.prenom} : ${etat.occupant.titre}`}
                className="min-h-[44px] font-semibold underline hover:text-primary-900"
              >
                Retirer
              </button>
            )}
            {etat.ton === 'panier' && (
              <button
                type="button"
                onClick={() => {
                  retirerDuPanier(etat.occupant);
                  focaliser(idTitre);
                }}
                aria-label={`Retirer du panier : ${etat.occupant.titre}, pour ${kid.prenom}`}
                className="min-h-[44px] font-semibold underline hover:text-primary-900"
              >
                Retirer du panier
              </button>
            )}
            {etat.ton === 'inscription' && (
              <Link to="/registrations" className="inline-flex min-h-[44px] items-center font-semibold underline">
                Voir ses inscriptions
              </Link>
            )}
          </div>
        </div>

        {parCentre === null ? (
          <div className="grid md:grid-cols-2 gap-6">{bloc.sessions.map((a) => carte(a, kid, bloc))}</div>
        ) : (
          parCentre.map((g) => (
            <div key={g.id} className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-700">
                {g.nom}
                {g.id === centreDeLEcole && <span className="font-normal text-gray-600"> · le centre de l’école de {kid.prenom}</span>}
              </h4>
              <div className="grid md:grid-cols-2 gap-6">{g.sessions.map((a) => carte(a, kid, bloc, 'h5'))}</div>
            </div>
          ))
        )}
      </section>
    );
  };

  const sectionEnfant = (kid: KidWithDetails) => {
    const etat = etatDe(kid);
    const age = ageEnAnnees(kid.date_naissance);
    const sessions = parEnfant[kid.id] ?? [];
    const acces = accesDeLEnfant(kid.inclusion);
    const semaines = grouperParSemaine(sessions, (a) => ({
      titre: a.stage.title,
      type: a.stage.type_activite ?? null,
      jours: a.nombre_jours ?? null,
    }));

    return (
      <section key={kid.id} aria-labelledby={`stages-${kid.id}`}>
        {/* Toujours dit : pour qui sont les semaines qu'on coche. */}
        <h2 id={`stages-${kid.id}`} tabIndex={-1} className="mb-4 text-2xl font-semibold text-gray-900">
          Les stages pour {kid.prenom}
          {age !== null && <span className="ml-2 text-base font-normal text-gray-500">{age} ans</span>}
        </h2>

        {etat.aFaire ? (
          <Link
            to={`/kids/${kid.id}/verifier`}
            className="flex min-h-[56px] items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 hover:bg-amber-100/50"
          >
            <span className="min-w-0">
              <span className="block font-medium text-gray-900">
                Avant de choisir une semaine pour {kid.prenom}
              </span>
              <span className="block text-sm text-gray-600">
                {etat.complet
                  ? 'À vérifier — ses informations datent de plus de six semaines'
                  : resumeEnfant(etat)}
              </span>
            </span>
            <span className="flex-shrink-0 text-sm font-medium text-amber-700">
              {etat.complet ? 'Vérifier' : 'Compléter'}
            </span>
          </Link>
        ) : acces === 'impossible' ? (
          /*
            Niveau 3 : ni inscription ni demande. Un encadré sobre remplace tout
            (décision du 26/10/2026) ; la raison a été écrite au parent avec la
            réponse à sa demande.
          */
          <div className="flex items-start gap-3 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-600" aria-hidden="true" />
            <div className="text-sm text-gray-800">
              <p className="font-medium">Nous ne pouvons pas accueillir {kid.prenom} dans nos stages pour le moment</p>
              <p className="mt-1">
                Si sa situation évolue, écrivez-nous à{' '}
                <a href="mailto:info@eclosion.be" className="font-medium text-primary-700 underline">info@eclosion.be</a>&nbsp;:
                nous reverrons ensemble ce qui est possible.
              </p>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-6 text-center text-gray-600">
            Aucun stage pour {kid.prenom} avec ces critères. Essayez un autre centre ou une autre
            période.
          </div>
        ) : (
          <>
            {/*
              Dit AVANT les semaines, une fois : avant, chaque carte portait son
              bouton et rien ne disait qu'une demande était partie.
            */}
            {acces === 'sur-demande' && (
              <div className="mb-6 flex items-start gap-3 rounded-xl bg-blue-50 p-4 ring-1 ring-blue-200">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium">Pour {kid.prenom}, une demande d’abord</p>
                  <p className="mt-1">
                    {kid.prenom} a des besoins particuliers&nbsp;: avant l’inscription, l’équipe vérifie
                    que nous pouvons bien l’accueillir. Cochez toutes ses semaines dont les inscriptions sont
                    ouvertes, puis «&nbsp;Envoyer la demande&nbsp;» — une seule fois, pour toutes. Si nous
                    pouvons l’accueillir, nous l’inscrivons et vous recevez la facture&nbsp;; sinon, nous vous
                    écrivons pourquoi.
                  </p>
                </div>
              </div>
            )}
            {inscriptionsIllisibles && (
              <p className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                Nous n’avons pas pu relire les inscriptions de {kid.prenom}&nbsp;: rechargez la page avant de
                choisir, pour ne pas prendre une semaine déjà réservée.
              </p>
            )}
            <p className="mb-6 flex items-start gap-2 text-sm text-gray-700">
              <CalendarDays className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" />
              Un stage par semaine pour {kid.prenom}. Pour changer, choisissez-en un autre à la place.
            </p>
            <div className="space-y-10">{semaines.map((bloc) => semaineDeStages(bloc, kid))}</div>
          </>
        )}
      </section>
    );
  };

  const boutonsVisibles = repartition.panier.length > 0 || aDemanderParEnfant.length > 0;

  return (
    <div className={clsx('container max-w-7xl mx-auto py-12 px-4', boutonsVisibles && 'pb-40')}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">Stages disponibles</h1>
      </div>

      {/* Ce qui change à l'écran, dit une fois aux lecteurs d'écran. */}
      <p className="sr-only" aria-live="polite">{annonce}</p>

      {bandeauVisible && arrivee.nouvelEnfant && (
        <div
          role="status"
          className="mb-8 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4"
        >
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-sm text-green-900">
            <p className="font-medium">
              La fiche de {arrivee.nouvelEnfant.prenom || 'votre enfant'} est enregistrée. Choisissez
              ses semaines ci-dessous.
            </p>
            {arrivee.photoManquee && (
              <p className="mt-1">
                La photo n’a pas pu être enregistrée&nbsp;: vous pourrez l’ajouter depuis sa fiche.
              </p>
            )}
            <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <Link to="/kids/new" className="font-medium underline hover:text-green-700">
                Ajouter un autre enfant
              </Link>
              <Link
                to={`/kids/${arrivee.nouvelEnfant.id}`}
                className="font-medium underline hover:text-green-700"
              >
                Voir sa fiche
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setBandeauVisible(false)}
            aria-label="Fermer"
            className="text-green-700 hover:text-green-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="sr-only">Vos critères</h2>

            {/*
              Trois critères dans l'ordre : l'enfant d'abord, qui décide du
              reste. Tant qu'il n'est pas choisi, le centre et la période
              attendent, grisés — les choisir avant n'aurait pas de sens : le
              centre proposé est celui de son école.
            */}
            <div className="space-y-6">
              <div>
                <label className="mb-1.5 flex items-center text-sm font-semibold text-gray-900" htmlFor="filtre-enfant">
                  <NumeroCritere numero={1} etat={enfant ? 'pret' : 'a-faire'} />
                  Quel enfant inscrivez-vous&nbsp;?
                </label>
                {kids.length === 0 ? (
                  <p className="text-sm text-gray-600">
                    <Link to="/kids/new" className="font-medium text-primary-600 underline">
                      Ajoutez votre premier enfant
                    </Link>{' '}
                    pour voir les stages de son âge.
                  </p>
                ) : (
                  <>
                    <select
                      id="filtre-enfant"
                      className={clsx('form-input', !enfant && 'border-primary-500 ring-2 ring-primary-200')}
                      value={enfantId ?? ''}
                      onChange={(e) => {
                        setCentreRepris(null);
                        setEnfantId(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        Choisir un enfant…
                      </option>
                      {kids.map((kid) => {
                        const age = ageEnAnnees(kid.date_naissance);
                        return (
                          <option key={kid.id} value={kid.id}>
                            {kid.prenom} {kid.nom}
                            {age !== null ? ` (${age} ans)` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {/* L'état est affiché : sinon le parent découvre après coup que la fiche n'est pas prête */}
                    {enfant && etatDe(enfant).aFaire && (
                      <p className="mt-1 text-xs text-amber-700">{resumeEnfant(etatDe(enfant))}</p>
                    )}
                    {/*
                      Discret : « + Ajouter un enfant », en couleur, attirait le
                      clic de parents dont les enfants étaient déjà là.
                    */}
                    <Link to="/kids/new" className="mt-1.5 inline-block text-xs text-gray-400 hover:text-gray-600 hover:underline">
                      Créer le profil d’un autre enfant
                    </Link>
                  </>
                )}
              </div>

              <div className={clsx(!enfant && 'opacity-50')}>
                <label className="mb-1.5 flex items-center text-sm font-semibold text-gray-900" htmlFor="filtre-centre">
                  <NumeroCritere numero={2} etat={enfant ? 'pret' : 'en-attente'} />
                  Centre
                </label>
                <select
                  id="filtre-centre"
                  className="form-input disabled:cursor-not-allowed"
                  value={centre}
                  disabled={!enfant}
                  onChange={(e) => {
                    setCentreChoisi(true);
                    setCentreRepris(null);
                    setCentre(e.target.value);
                  }}
                >
                  <option value="">Tous les centres</option>
                  {centers.map((center) => (
                    <option key={center.id} value={center.id}>
                      {center.name}
                    </option>
                  ))}
                </select>
                {enfant && centre && !centreChoisi && !centreRepris && (
                  <p className="mt-1 text-xs text-gray-500">Proposé d’après l’école de {enfant.prenom}.</p>
                )}
                {enfant && centre && centreRepris && centreRepris !== enfant.prenom && (
                  <p className="mt-1 text-xs text-gray-500">Le même centre que pour {centreRepris}.</p>
                )}
              </div>

              <div className={clsx(!enfant && 'opacity-50')}>
                <label className="mb-1.5 flex items-center text-sm font-semibold text-gray-900" htmlFor="filtre-periode">
                  <NumeroCritere numero={3} etat={enfant ? 'pret' : 'en-attente'} />
                  Période
                </label>
                <select
                  id="filtre-periode"
                  className="form-input disabled:cursor-not-allowed"
                  value={periode}
                  disabled={!enfant}
                  onChange={(e) => setPeriode(e.target.value)}
                >
                  {periodes.length === 0 && <option value="">Aucune période à venir</option>}
                  {periodes.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                {enfant && periode && periode === periodes[0] && (
                  <p className="mt-1 text-xs text-gray-500">La prochaine période de stages.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {!etatProfil.complet ? (
            <div className="rounded-xl bg-amber-50 p-6 ring-1 ring-amber-200 sm:p-8">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-900">
                <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                Avant de choisir une semaine
              </h3>
              <p className="mt-1 text-sm text-amber-900/80">
                Nous avons besoin de vos coordonnées pour accueillir votre enfant. Une fois
                complétées, les stages s’affichent ici.
              </p>
              <Link
                to="/bienvenue?suite=/activities"
                className="mt-4 flex min-h-[56px] items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-amber-200 hover:bg-amber-100/50"
              >
                <span className="min-w-0">
                  <span className="block font-medium text-gray-900">Vos coordonnées</span>
                  <span className="block text-sm text-gray-600">
                    {etatProfil.bloquants.length === 1
                      ? '1 information à compléter'
                      : `${etatProfil.bloquants.length} informations à compléter`}
                  </span>
                </span>
                <span className="flex-shrink-0 text-sm font-medium text-amber-700">Compléter</span>
              </Link>
            </div>
          ) : !enfant ? (
            kids.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                {/* La flèche montre où commencer : au-dessus sur un téléphone, à gauche sur un écran large. */}
                <ArrowUp className="mx-auto mb-4 h-10 w-10 text-primary-500 lg:hidden" aria-hidden="true" />
                <ArrowLeft className="mx-auto mb-4 hidden h-10 w-10 text-primary-500 lg:block" aria-hidden="true" />
                <h3 className="text-lg font-semibold mb-2">Commencez par choisir l’enfant à inscrire</h3>
                <p className="text-gray-600">
                  Étape 1, «&nbsp;Quel enfant inscrivez-vous&nbsp;?&nbsp;». Le centre et la période
                  suivent&nbsp;: nous vous proposons le centre de son école et la prochaine période.
                </p>
              </div>
            )
          ) : chargement ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : erreur ? (
            <div className="bg-white rounded-xl shadow-md p-8 text-center text-red-600">
              <AlertCircle className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Une erreur est survenue</h3>
              <p>{erreur}</p>
            </div>
          ) : (
            <div className="space-y-10">{coches.map(sectionEnfant)}</div>
          )}

          {/* Une semaine déjà demandée ou non retenue reste parfois cochée : elle ne fait pas apparaître de bouton sans effet. */}
          {boutonsVisibles && (
            <div className="fixed bottom-8 right-8 z-30 flex flex-col items-end gap-1">
              {/* Les semaines « sur demande » partent en demande, une par enfant ; le reste va au panier. */}
              {aDemanderParEnfant.length > 0 && (
                <button
                  type="button"
                  onClick={envoyerLesDemandes}
                  disabled={envoiEnCours || ajoutEnCours}
                  className="btn-primary flex items-center space-x-2 shadow-lg"
                >
                  {envoiEnCours ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  <span>
                    {/* Court, pour tenir sur un téléphone : la pastille du dessous dit pour qui. */}
                    {aDemanderParEnfant.length === 1
                      ? `Envoyer la demande (${aDemanderParEnfant[0].n} semaine${aDemanderParEnfant[0].n > 1 ? 's' : ''})`
                      : 'Envoyer les demandes'}
                  </span>
                </button>
              )}
              {repartition.panier.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={envoiEnCours || ajoutEnCours}
                  className={clsx(
                    'flex items-center space-x-2 shadow-lg',
                    aDemanderParEnfant.length > 0 ? 'btn-outline bg-white' : 'btn-primary',
                  )}
                >
                  {ajoutEnCours ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}
                  <span>
                    Ajouter {repartition.panier.length} stage{repartition.panier.length > 1 ? 's' : ''} au panier
                  </span>
                </button>
              )}
              {/* Après un changement d'enfant, les semaines du précédent restent choisies : on dit pour qui. */}
              <span className="rounded-full bg-white/95 px-3 py-1 text-xs text-gray-700 shadow">
                {choisiesParEnfant.map((e) => `${e.prenom} : ${e.n}`).join(' · ')}
              </span>
            </div>
          )}
        </div>
      </div>

      <ActivityModal activity={selectedActivity} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/*
        Après « Ajouter au panier », un compte de plusieurs enfants : inscrire
        le suivant, dans le même centre (VALIDÉ ÉCLOSION, 30/10/2026). Fermer la
        fenêtre laisse sur la page ; le panier est dans la barre du haut.
      */}
      <Dialog open={proposition !== null} onClose={() => setProposition(null)} className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-end justify-center p-4 sm:items-center">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          {proposition && (
            <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 flex-shrink-0 text-green-600" aria-hidden="true" />
                <div>
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {proposition.faits.length === 1
                      ? `Les stages de ${kids.find((k) => k.id === proposition.faits[0])?.prenom ?? 'votre enfant'} sont dans votre panier`
                      : 'Vos stages sont dans votre panier'}
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 text-gray-700">
                    Voulez-vous aussi inscrire {proposition.suivant.prenom}&nbsp;?
                  </Dialog.Description>
                  <p className="mt-1 text-sm text-gray-600">
                    {centre
                      ? `Nous gardons le même centre (${centers.find((x) => x.id === centre)?.name ?? ''}) et la même période.`
                      : 'Nous gardons les mêmes critères : tous les centres, même période.'}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
                <button type="button" onClick={inscrireLeSuivant} className="btn-primary min-h-[48px] flex-1">
                  Oui, inscrire {proposition.suivant.prenom}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProposition(null);
                    navigate('/cart');
                  }}
                  className="btn-outline min-h-[48px] flex-1"
                >
                  Non, voir le panier
                </button>
              </div>
            </div>
          )}
        </div>
      </Dialog>

      <Toaster position="top-right" />
    </div>
  );
};

export default ActivitiesPage;
